import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Box, Divider, Grid, Paper, Typography } from "@mui/material";
import LifestyleCharts from "../containers/Lifestyle/LifestyleCharts";
import * as patientActions from "../redux/actions/patientActions";
import { store } from "../redux/store";
import { useSelector } from "react-redux";
import { ConsultationContext } from "../context/ConsultationContext";
import { useContext } from "react";
import { useLocation } from "react-router-dom";
import Select from 'react-select'
import Scrollbar from "../containers/Anatomy/Scrollbar";
import { CircularProgress } from '@mui/material';
import usePermissions from "../utils/permissions";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const paperStyle = {
  // backdropFilter: "blur(10px)",
  // WebkitBackdropFilter: "blur(10px)",
  borderRadius: '20px',
  textAlign: "center",
  mt: 1,
  border:"1px solid #E5E7EB"
};

const selectStyle = {
  container: (base) => ({
    ...base,
    flex: 1,
  }),
  control: (base) => ({
    ...base,
    border:"1px solid #E5E7EB",
    padding: 0,
    // boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
    borderRadius: '5px',
    height: '27px',
    minHeight: '27px',
    alignItems: 'center',
  }),
  valueContainer: (base) => ({
    ...base,
    height: '100%',
    padding: '0 8px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '100%',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    fontSize: '14px',
    height: '27px',
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: '14px',
    lineHeight: '27px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '0 4px',
    display: 'flex',
    alignItems: 'center',
    color: "#869094",
    '& svg': {
      width: 17,
      height: 17,
    },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 0,
    marginTop: 0,
    backgroundColor: '#FFF',
  }),
  option: (styles, state) => ({
    ...styles,
    backgroundColor: state.isSelected
      ? "grey"
      : state.isFocused
        ? "#D3D3D3"
        : "transparent",
    color: state.isSelected ? "#fff" : "black",
  }),
};

function mergeAnswersWithSchedules(item) {
  const answers = Array.isArray(item?.answers) ? item.answers : [];
  const schedules = Array.isArray(item?.consultationSchedules)
    ? item.consultationSchedules
    : [];

  const answersMap = new Map(
    answers.map((a) => [
      Number(a.assessmentWeek),
      { ...a, week: Number(a.assessmentWeek) },
    ])
  );

  const mergedAnswers = schedules?.map((s) => {
    const wk = Number(s.week);
    if (answersMap.has(wk)) {
      return answersMap.get(wk); // already normalized to have "week"
    }
    return {
      week: wk,
      trimester: s.trimester,
      answer: null,
      minValue: 0,
      maxValue: 10,
    };
  });

  return { ...item, answers: mergedAnswers };
}

const Lifestyle = () => {
  const { patientId, consultationId } = useParams();
  const location = useLocation();
  const [edit, setEdit] = useState(true);
  const { setChildData } = useContext(ConsultationContext);
  const [loader, setLoader] = useState(true)
  const containerRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const mode = queryParams.get("mode");
    if (mode) {
      setEdit(mode === "view" ? false : hasPermission("LEP_CREATE") ? true : false);
    }
  }, [location.search]);

  const patientReducer = useSelector((state) => state.patientReducer); // reducer for picking up tthe states
  const dietPlan = patientReducer?.dietPlan; // used in render nethod 
  const lifestyleGraphs = patientReducer?.lifestyleGraphs;

  useEffect(() => {
    if (patientId) {
      store.dispatch(patientActions.getLifestyleChartsRequestAction(patientId));
    }
  }, [patientId]);

  useEffect(() => {
    store.dispatch(
      patientActions.getRecommendedDietPlanRequestAction(consultationId)
    );
    store.dispatch(
      patientActions.getRecommendedLifestyleRequestAction(consultationId)
    );
  }, []);


  const [updateLifestyle, setUpdateLifestyle] = useState({})

  useEffect(() => {
    setChildData(updateLifestyle)
  }, [updateLifestyle]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const el = containerRef.current;
      if (el) {
        setIsOverflowing(el.scrollHeight > el.clientHeight);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [updateLifestyle?.dietPlans]);


  const sleepData = lifestyleGraphs?.find(
    (item) => item.graphLabel === "SLEEP"
  );

  const waterData = lifestyleGraphs?.find(
    (item) => item.graphLabel === "WATER_INTAKE"
  );

  const sugarData = lifestyleGraphs?.find(
    (item) => item.graphLabel === "SUGAR_INTAKE"
  );

  const smokingData = lifestyleGraphs?.find(
    (item) => item.graphLabel === "SMOKING"
  );

  const sleepChartData = mergeAnswersWithSchedules(
    lifestyleGraphs?.find((item) => item.graphLabel === "SLEEP")
  );
  const waterChartData = mergeAnswersWithSchedules(
    lifestyleGraphs?.find((item) => item.graphLabel === "WATER_INTAKE")
  );
  const sugarChartData = mergeAnswersWithSchedules(
    lifestyleGraphs?.find((item) => item.graphLabel === "SUGAR_INTAKE")
  );
  const smokingChartData = mergeAnswersWithSchedules(
    lifestyleGraphs?.find((item) => item.graphLabel === "SMOKING")
  );

  const handleSugarChange = (event) => {
    const value = event.target.value;
    setUpdateLifestyle(prev => ({
      ...prev,
      sugarIntake: value,
    }));
  };
  const handleSugarNotesChange = (event) => {
    const value = event.target.value;
    setUpdateLifestyle(prev => ({
      ...prev,
      sugarIntakeNotes: value,
    }));

  };

  const handleSleepChange = (event) => {
    const value = event.target.value;
    setUpdateLifestyle(prev => ({
      ...prev,
      sleep: value,
    }));
  };
  const handleSleepNotesChange = (event) => {
    const value = event.target.value;
    setUpdateLifestyle(prev => ({
      ...prev,
      sleepNotes: value,
    }));
  };

  const handleSmokingChange = (event) => {
    const value = event.target.value;
    setUpdateLifestyle(prev => ({
      ...prev,
      smoking: value,
    }));
  };
  const handleSmokingNotesChange = (event) => {
    const value = event.target.value;
    setUpdateLifestyle(prev => ({
      ...prev,
      smokingNotes: value,
    }));
  };

  const handleWaterChange = (event) => {
    const value = event.target.value;
    setUpdateLifestyle(prev => ({
      ...prev,
      waterIntake: value,
    }));
  };
  const handleWaterNotesChange = (event) => {
    const value = event.target.value;
    setUpdateLifestyle(prev => ({
      ...prev,
      waterIntakeNotes: value,
    }));
  };

  const chartProps = {
    patientId,
    lifestyleGraphs,
    sugarIntake: updateLifestyle.sugarIntake || '',
    sugarNotes: updateLifestyle.sugarIntakeNotes || '',
    sugarData,
    sleepDuration: updateLifestyle.sleep || '',
    sleepNotes: updateLifestyle.sleepNotes || '',
    sleepData,
    smokingStatus: updateLifestyle.smoking || '',
    smokingNotes: updateLifestyle.smokingNotes || '',
    smokingData,
    waterIntake: updateLifestyle.waterIntake || '',
    waterNotes: updateLifestyle.waterIntakeNotes || '',
    waterData,
    sleepChartData,
    smokingChartData,
    waterChartData,
    sugarChartData,
    handleSugarChange,
    handleSugarNotesChange,
    handleSleepChange,
    handleSleepNotesChange,
    handleSmokingChange,
    handleSmokingNotesChange,
    handleWaterChange,
    handleWaterNotesChange,
    patientReducer,
    edit,
  };

  const frequency = [

    {
      "label": "Once",
      "value": "ONCE"
    },
    {
      "label": "2 to 3 Times",
      "value": "TWO_TO_THREE_TIMES"
    },
    {
      "label": "Everyday",
      "value": "EVERYDAY"
    }
  ]

  const handleChange = (stage, nutrientId, foodId, value, diet, nutrient) => {
    // Ensure dietPlans exists
    if (!updateLifestyle.dietPlans) {
      updateLifestyle.dietPlans = [];
    }
    // Find the stage
    const stageIndex = updateLifestyle.dietPlans.findIndex((item) => item.stage === stage);
    // If the stage doesn't exist, add it
    if (stageIndex === -1) {
      updateLifestyle.dietPlans.push({
        stage,
        description: diet.description,
        dietType: diet.dietType,
        nutrients: [
          {
            id: nutrientId,
            name: nutrient.name,
            foods: foodId === null || value.name === "None" ? [] : [
              value
            ],
          },
        ],
      });
    } else {
      // Stage found, find the nutrient
      const nutrientIndex = updateLifestyle.dietPlans[stageIndex].nutrients.findIndex(
        (nutrient) => nutrient.id === nutrientId
      );
      if (nutrientIndex === -1) {
        // If nutrient doesn't exist in the stage, add it
        updateLifestyle.dietPlans[stageIndex].nutrients.push({
          id: nutrientId,
          name: nutrient.name,
          foods: foodId === null || value.name === "None" ? [] : [
            value
          ],
        });
      } else {
        // Nutrient found, check if the food exists
        const foodIndex = updateLifestyle.dietPlans[stageIndex].nutrients[nutrientIndex].foods.findIndex(
          (food) => food.id === foodId
        );
        if (foodId === null || value.name === "None") {
          // If the food is "None" or foodId is null, remove the specific food
          if (foodIndex !== -1) {
            updateLifestyle.dietPlans[stageIndex].nutrients[nutrientIndex].foods.splice(foodIndex, 1);
          }
        } else {
          // If food is selected and not "None"
          if (foodIndex === -1) {
            // Food doesn't exist, add it
            updateLifestyle.dietPlans[stageIndex].nutrients[nutrientIndex].foods.push(
              value
            );
          } else {
            // Food exists, update it
            updateLifestyle.dietPlans[stageIndex].nutrients[nutrientIndex].foods[foodIndex] = {
              ...updateLifestyle.dietPlans[stageIndex].nutrients[nutrientIndex].foods[foodIndex],
              ...value,
              updatedAt: new Date().toISOString(),
            };
          }
        }
        // Check if foods array is empty, remove the nutrient if it is
        if (updateLifestyle.dietPlans[stageIndex].nutrients[nutrientIndex].foods.length === 0) {
          updateLifestyle.dietPlans[stageIndex].nutrients.splice(nutrientIndex, 1);
        }
      }
      // Check if nutrients array is empty, remove the stage if it is
      if (updateLifestyle.dietPlans[stageIndex].nutrients.length === 0) {
        updateLifestyle.dietPlans.splice(stageIndex, 1);
      }
    }
    setUpdateLifestyle({ ...updateLifestyle });
  };

  const handleChangeOtherValue = (stage, nutrientId, foodId, value,) => {
    const stageIndex = updateLifestyle.dietPlans.findIndex((item) => item.stage === stage);
    const nutrientIndex = updateLifestyle.dietPlans[stageIndex].nutrients.findIndex(
      (nutrient) => nutrient.id === nutrientId
    );
    const foodIndex = updateLifestyle.dietPlans[stageIndex].nutrients[nutrientIndex].foods.findIndex(
      (food) => food.id === foodId
    );
    updateLifestyle.dietPlans[stageIndex].nutrients[nutrientIndex].foods[foodIndex] = {
      ...updateLifestyle.dietPlans[stageIndex].nutrients[nutrientIndex].foods[foodIndex],
      ...value
    }
    setUpdateLifestyle({ ...updateLifestyle });
  };

  useEffect(() => {
    if (patientReducer.dietPlan) {
      const cleanedData = (patientReducer.dietPlan || []).map(stage => ({
        ...stage,
        nutrients: stage.nutrients.map(nutrient => ({
          ...nutrient,
          foods: nutrient.foods.filter(food => food.id !== null)
        }))
      }))
      setUpdateLifestyle({
        dietPlans: cleanedData
      })
    }
    if (patientReducer.recommendedLifestyle) {
      setUpdateLifestyle({
        dietPlans: patientReducer.recommendedLifestyle?.diet || [],
        "sleep": patientReducer.recommendedLifestyle.sleep || '',
        "sugarIntake": patientReducer.recommendedLifestyle.sugarIntake || '',
        "waterIntake": patientReducer.recommendedLifestyle.waterIntake || '',
        "smoking": patientReducer.recommendedLifestyle.smoking || '',
        "sleepNotes": patientReducer.recommendedLifestyle.sleepNotes || '',
        "waterIntakeNotes": patientReducer.recommendedLifestyle.waterIntakeNotes || '',
        "smokingNotes": patientReducer.recommendedLifestyle.smokingNotes || '',
        "sugarIntakeNotes": patientReducer.recommendedLifestyle.sugarIntakeNotes || '',
      })
    }
  }, [patientReducer.recommendedLifestyle, patientReducer.dietPlan])


  useEffect(() => {
    if (consultationId) {
      const timeout = setTimeout(() => {
        setLoader(false);
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [consultationId]);


  if (loader) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const renderTable = (withScrollbar = false) => {
    return <Box sx={{
      ...(withScrollbar ? {} : {  overflowY: 'auto' }),
      p: 1,
      pb: 2,
      borderRadius: '10px',
    }} ref={containerRef}>
      {
        edit ? (dietPlan || []).map((diet, dietIndex) => {
          const isLastDiet = dietIndex === (dietPlan || []).length - 1;
          return <Grid container spacing={1} sx={{
            // boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
            // backgroundColor: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '10px',
            p: 1,
            mt: 1,
            mb: isLastDiet ? 1 : 0,
            border:"1px solid #E5E7EB"
          }} >
            <Grid size={{ md: 12, lg: 12, sm: 12, xs: 12 }}>
              <Grid container spacing={1}>
                <Grid size={{ lg: 3, md: 3 }}>
                  <Typography sx={{ fontWeight: 'bold', fontSize: { xl: '18px', lg: '18px', md: '14px' } }}>
                    {diet.stage}
                  </Typography>

                </Grid>
                <Grid size={{ lg: 9, md: 9 }} >
                  <Typography sx={{ fontSize: { xl: '13px', lg: '13px', md: '11px' } }} textAlign={'left'} >
                    {diet.description}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            {
              <Grid size={{ md: 12, lg: 12, sm: 12, xs: 12 }}>
                <Grid container spacing={1} >
                  <Grid size={{ xl: 2, lg: 2, md: 2 }}>
                  </Grid>
                  <Grid size={{ xl: 2.5, lg: 2.5, md: 2.5 }} justifyItems={'center'}>
                    <Typography fontSize={"10px"} sx={{ backgroundColor: 'rgba(221, 238, 253, 1)', width: '110px', height: '20px', borderRadius: '10px' }} alignContent={'center'}>
                      Recommendation
                    </Typography>
                  </Grid>
                  <Grid size={{ xl: 1.5, lg: 1.5, md: 1.5 }} justifyItems={'center'} >
                    <Typography fontSize={"10px"} sx={{ backgroundColor: 'rgba(221, 238, 253, 1)', width: '72px', height: '20px', borderRadius: '10px' }} alignContent={'center'}>
                      Serving unit
                    </Typography>
                  </Grid>
                  <Grid size={{ xl: 1, lg: 1, md: 1 }} justifyItems={'center'} >
                    <Typography fontSize={"10px"} sx={{ backgroundColor: 'rgba(221, 238, 253, 1)', width: '42px', height: '20px', borderRadius: '10px' }} alignContent={'center'}>
                      % DV
                    </Typography>
                  </Grid>
                  <Grid size={{ xl: 1, lg: 1, md: 1 }} justifyItems={'center'}>
                    <Typography fontSize={"10px"} sx={{ backgroundColor: 'rgba(221, 238, 253, 1)', width: '38px', height: '20px', borderRadius: '10px' }} alignContent={'center'}>
                      Qty
                    </Typography>
                  </Grid>
                  <Grid size={{ xl: 4, lg: 4, md: 4 }} justifyItems={'center'}>
                    <Typography fontSize={"10px"} sx={{ backgroundColor: 'rgba(221, 238, 253, 1)', width: '124px', height: '20px', borderRadius: '10px' }} alignContent={'center'}>
                      Frequencey per week
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            }
            {
              diet.nutrients.map((nutrient) => {
                return <Grid size={{ lg: 12, xl: 12, md: 12 }}>
                  <Grid container spacing={1}>
                    <Grid size={{ xl: 2, lg: 2, md: 2 }} alignSelf={"center"}>
                      <Typography sx={{ textAlign: 'center', fontSize: '13px' }} >
                        {nutrient.name}
                      </Typography>
                    </Grid>
                    <Grid size={{ lg: 10, xl: 10, md: 10 }}>
                      {nutrient.foods.map((food) => {
                        const nutrientInLifestyle = updateLifestyle?.dietPlans
                          ?.find((di) => di.stage === diet.stage)
                          ?.nutrients.find((n) => n.id === nutrient.id);

                        const foodInLifestyle = nutrientInLifestyle?.foods.find((f) => f.id === food.id);
                        const updatedFood = foodInLifestyle || food;
                        const foodExistsInLifestyle = nutrientInLifestyle?.foods.some((f) => f.id === food.id);
                        const filteredOptions = nutrient?.foods.filter(
                          (item) => item.id === food.id || item.id === null
                        );

                        const selectedValue = foodExistsInLifestyle
                          ? updatedFood
                          : filteredOptions?.find((item) => item.id === null);

                        return food.id !== null && <Grid container spacing={1} mt={0.5}>
                          <Grid size={{ lg: 3, xl: 3, md: 3 }}>
                            <Select
                              onChange={(event) => {
                                handleChange(diet.stage, nutrient.id, food.id, event, diet, nutrient)
                              }}
                              value={selectedValue}
                              getOptionLabel={option => {
                                return option.name
                              }}
                              getOptionValue={option => {
                                return option.name
                              }}
                              options={filteredOptions}
                              isSearchable={false}
                              styles={selectStyle}
                            />
                          </Grid>
                          <Grid size={{ lg: 1.8, xl: 1.8, md: 1.8 }} sx={{ fontSize: '12px' }} alignSelf={'center'}>
                            <Typography sx={{ fontSize: '12px' }}  >
                              {selectedValue.servingUnit}
                            </Typography>
                          </Grid>
                          <Grid size={{ lg: 1.2, xl: 1.2, md: 1.2 }} sx={{ fontSize: '12px' }} alignSelf={'center'}>
                            <Typography sx={{ fontSize: '12px' }} >
                              {selectedValue.dailyValue}
                            </Typography>
                          </Grid>
                          <Grid size={{ lg: 1.2, xl: 1.2, md: 1.2 }}>
                            {selectedValue.name !== "None" && <Select
                              onChange={(event) => {
                                handleChangeOtherValue(diet.stage, nutrient.id, food.id, event)
                              }}
                              value={{ quantity: selectedValue.quantity }}
                              getOptionLabel={option => {
                                return option.quantity
                              }}
                              getOptionValue={option => {
                                return option.quantity
                              }}
                              options={[{ quantity: 1 }, { quantity: 2 }]}
                              isSearchable={false}
                              styles={selectStyle}
                            />}
                          </Grid>

                          <Grid size={{ lg: 4.8, xl: 4.8, md: 4.8 }} sx={{ display: 'flex', flexDirection: 'row', alignSelf: 'center' }} justifyContent={'center'}>
                            {
                              selectedValue.name !== "None" && frequency.map((freq, index) => {
                                const isLast = index === frequency.length - 1;
                                return (
                                  <Box sx={{ display: 'flex', flexDirection: 'row', alignSelf: 'center', ml: 0.5 }}>
                                    <Typography
                                      fontSize={'12px'}
                                      sx={{ backgroundColor: selectedValue.frequency === freq.value ? '#FFF' : 'transparent', height: '20px', px: 1, borderRadius: '10px', cursor: 'pointer' }}
                                      onClick={() => {
                                        handleChangeOtherValue(diet.stage, nutrient.id, food.id, { frequency: freq.value });
                                      }}
                                    >
                                      {freq.label}
                                    </Typography>
                                    {!isLast && <Divider sx={{ width: '1px', height: '20px', backgroundColor: 'black', ml: 0.5 }} />}
                                  </Box>
                                );
                              })
                            }
                          </Grid>
                        </Grid>
                      })
                      }
                    </Grid>
                  </Grid>
                </Grid>
              })
            }
          </Grid>
        })
          :
          updateLifestyle?.dietPlans.map((diet, dietIndex) => {
            const isLastDiet = dietIndex === (updateLifestyle?.dietPlans || []).length - 1;
            return <Grid container spacing={1} sx={{
              // boxShadow: '0 3px 4px rgba(0,0,0,0.15)',
              // backgroundColor: '#E9F7FB',
              borderRadius: '10px',
              p: 1,
              mt: 1,
              mb: isLastDiet ? 1 : 0,
              border:"1px solid #E5E7EB"
            }} >
              <Grid size={{ md: 12, lg: 12, sm: 12, xs: 12 }} >
                <Grid container spacing={1}>
                  <Grid size={{ lg: 3, md: 3 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: { xl: '18px', lg: '18px', md: '14px' } }}>
                      {diet.stage}
                    </Typography>

                  </Grid>
                  <Grid size={{ lg: 9, md: 9 }} >
                    <Typography sx={{ fontSize: { xl: '10px', lg: '10px', md: '9px' } }} textAlign={'left'} >
                      {diet.description}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {
                <Grid size={{ md: 12, lg: 12, sm: 12, xs: 12 }} >
                  <Grid container spacing={1} >
                    <Grid size={{ lg: 2, md: 2, xl: 2 }} >
                    </Grid>
                    <Grid size={{ xl: 2.5, lg: 2.5, md: 2.5 }} justifyItems={'center'}>
                      <Typography fontSize={"10px"} sx={{ backgroundColor: 'rgba(221, 238, 253, 1)', width: '110px', height: '20px', borderRadius: '10px' }} alignContent={'center'}>
                        Recommendation
                      </Typography>
                    </Grid>
                    <Grid size={{ xl: 1.5, lg: 1.5, md: 1.5 }} justifyItems={'center'} >
                      <Typography fontSize={"10px"} sx={{ backgroundColor: 'rgba(221, 238, 253, 1)', width: '72px', height: '20px', borderRadius: '10px' }} alignContent={'center'}>
                        Serving unit
                      </Typography>
                    </Grid>
                    <Grid size={{ xl: 1, lg: 1, md: 1 }} justifyItems={'center'} >
                      <Typography fontSize={"10px"} sx={{ backgroundColor: 'rgba(221, 238, 253, 1)', width: '42px', height: '20px', borderRadius: '10px' }} alignContent={'center'}>
                        % DV
                      </Typography>
                    </Grid>
                    <Grid size={{ xl: 1, lg: 1, md: 1 }} justifyItems={'center'}>
                      <Typography fontSize={"10px"} sx={{ backgroundColor: 'rgba(221, 238, 253, 1)', width: '38px', height: '20px', borderRadius: '10px' }} alignContent={'center'}>
                        Qty
                      </Typography>
                    </Grid>
                    <Grid size={{ xl: 4, lg: 4, md: 4 }}>
                      <Typography fontSize={"10px"} sx={{ backgroundColor: 'rgba(221, 238, 253, 1)', width: '124px', height: '20px', borderRadius: '10px' }} alignContent={'center'}>
                        Frequencey per week
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              }
              {
                diet.nutrients.map((nutrient) => {
                  return <Grid size={{ lg: 12, xl: 12, md: 12 }}>
                    <Grid container spacing={1}>
                      <Grid size={{ xl: 2, lg: 2, md: 2 }} alignSelf={"center"}>
                        <Typography sx={{ textAlign: 'center', fontSize: '13px' }} >
                          {nutrient.name}
                        </Typography>
                      </Grid>

                      <Grid size={{ lg: 10, xl: 10, md: 10 }}>
                        {nutrient.foods.map((food) => {
                          return <Grid container spacing={1} mt={0.5}>
                            <Grid size={{ lg: 3, xl: 3, md: 3 }} >
                              <Typography sx={{ backgroundColor: '#FFF', fontSize: '12px', minHeight: '27px', borderRadius: '5px', alignContent: 'center' }}
                              >
                                {food.name}
                              </Typography>
                            </Grid>
                            <Grid size={{ lg: 1.8, xl: 1.8, md: 1.8 }} sx={{ fontSize: '12px' }} alignSelf={'center'}>
                              <Typography sx={{ fontSize: '12px' }}  >
                                {food.servingUnit}
                              </Typography>
                            </Grid>
                            <Grid size={{ lg: 1.2, xl: 1.2, md: 1.2 }} sx={{ fontSize: '12px' }} alignSelf={'center'}>
                              <Typography sx={{ fontSize: '12px' }} >
                                {food.dailyValue}
                              </Typography>
                            </Grid>
                            <Grid size={{ lg: 1.2, xl: 1.2, md: 1.2 }}>
                              <Typography sx={{ fontSize: '12px' }}  >
                                {food.quantity}
                              </Typography>
                            </Grid>
                            <Grid size={{ lg: 4.8, xl: 4.8, md: 4.8 }} sx={{ display: 'flex', flexDirection: 'row', alignSelf: 'center' }} >
                              <Typography sx={{ backgroundColor: '#FFF', fontSize: '12px', height: '20px', px: 1, borderRadius: '5px' }}  >
                                {frequency.find((item) => item.value == food.frequency)?.label}
                              </Typography>
                            </Grid>
                          </Grid>
                        })}
                      </Grid>
                    </Grid>
                  </Grid>
                })}

            </Grid>
          })
      }
    </Box>
  }

  const renderContent = () => {
    return <Grid container sx={{ height: { xl: "80vh", lg: '80vh', md: '78vh' }, }}>
      <Grid size={{ lg: 12, xl: 8, md: 12 }} >
        <Grid container >
          <Grid size={{ md: 12, lg: 12, sm: 12, xs: 12 }} sx={{ p: 1 }}>
            <Grid container spacing={1} >
              <Grid>
                <Typography fontSize={"22px"}>
                  Nutrition
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ md: 12, lg: 12, sm: 12, xs: 12 }} sx={{ borderRadius: '10px',  m: 1, p: 0.3, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {
              isOverflowing ?
                <Box sx={{ flex: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {isMobile ? renderTable(true) : <Scrollbar
                    gridTemplate="auto / 1fr 20px"
                    padding="0 0rem 1rem 0rem"
                    scrollerPadding="0rem"
                    scrollerWidth="10px"
                    scrollHeight="20%"
                    style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                  >
                    <Box sx={{ height: '100%', }}>
                      {renderTable(true)}
                    </Box>
                  </Scrollbar>}
                </Box> :
                renderTable(false)
            }
          </Grid>
        </Grid>
      </Grid>
      {lifestyleGraphs?.length > 0 && <Grid size={{ xl: 4, lg: 12, md: 12 }} sx={{}} >
        <Grid container spacing={2}>
          <Grid size={{ md: 12, lg: 12, sm: 12, xs: 12 }}>
          </Grid>
          <Grid size={{ md: 12, lg: 12, sm: 12, xs: 12 }}>
            <LifestyleCharts {...chartProps} />
          </Grid>
        </Grid>
      </Grid>}
    </Grid>
  }

  return (

    <Box sx={{ px: 2, }}>

      <Paper
        elevation={5}
        sx={{
          ...paperStyle,
          height: { xl: "83vh", lg: '85vh', md: '80vh' },
          boxShadow:'none'
          // overflow: 'hidden'
        }}
      >
        {/* <Box sx={{ height:{xl:"100vh",lg:'88vh'}}}> */}
        {isMobile ? renderContent() : <Scrollbar gridTemplate="auto / 1fr 20px" padding="0 0rem" scrollerPadding="0rem" scrollerWidth="12px">
          {renderContent()}
        </Scrollbar>}
        {/* </Box> */}

      </Paper>
    </Box>

  );
};

export default Lifestyle;
